'use client';

import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { UnverifiedAssetConclusionDto } from '@terra/shared-contracts-assets';
import { MarkdownPreview } from '../../markdown-preview/MarkdownPreview';
import { BodyDefault, HeadingSection } from '../../../components/typography';
import { UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE } from './unverified-asset-typography';

type UnverifiedAssetConclusionProps = {
  conclusion: UnverifiedAssetConclusionDto;
  hideTitle?: boolean;
  variant?: 'page' | 'drawer';
};

const buildConclusionMarkdown = ({
  summary,
  evidence,
}: UnverifiedAssetConclusionDto): string =>
  evidence?.trim() ? `${summary}\n\n${evidence.trim()}` : summary;

const conclusionMarkdownClass = css({
  '& .wmde-markdown': {
    fontFamily: 'inherit !important',
    fontSize: 'inherit !important',
    lineHeight: 'inherit !important',
    letterSpacing: 'inherit !important',
    color: 'inherit',
  },
  '& .wmde-markdown p, & .wmde-markdown li': {
    fontFamily: 'inherit !important',
    fontSize: 'inherit !important',
    lineHeight: 'inherit !important',
    letterSpacing: 'inherit !important',
  },
  '& .wmde-markdown strong': {
    fontWeight: 'semibold',
  },
});

export function UnverifiedAssetConclusion({
  conclusion,
  hideTitle = false,
  variant = 'page',
}: UnverifiedAssetConclusionProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const markdown = buildConclusionMarkdown(conclusion);
  const isDrawer = variant === 'drawer';

  const markdownBody = (
    <BodyDefault as="div" w="full" className={conclusionMarkdownClass}>
      <MarkdownPreview
        source={markdown}
        fontSize={UNVERIFIED_ASSET_MARKDOWN_FONT_SIZE}
      />
    </BodyDefault>
  );

  return (
    <VStack
      gap="2"
      alignItems="stretch"
      w="full"
      px={hideTitle ? '0' : '4'}
      pb={hideTitle ? '0' : '3'}
    >
      {hideTitle ? null : (
        <HeadingSection color="fg.default">{t('conclusion')}</HeadingSection>
      )}
      {isDrawer ? (
        <HStack
          alignItems="stretch"
          gap="3"
          w="full"
          p="4"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.neutral.muted"
          borderRadius="md"
          bg="transparent"
        >
          <Box
            w="3px"
            flexShrink={0}
            borderRadius="full"
            bg="bg.danger"
            aria-hidden
          />
          <Box flex="1" minW={0} color="fg.default">
            {markdownBody}
          </Box>
        </HStack>
      ) : (
        <Box w="full" color="fg.muted">
          {markdownBody}
        </Box>
      )}
    </VStack>
  );
}
